import React, { useCallback, useMemo, useState } from 'react';
import { ChatWorkspaceComposerSection } from '@/chat/ui/index.js';
import useComposerModeSuggestion from './mode-suggestion.js';
import {
  buildComposerExecutionOptions,
  resolveComposerModeKey,
} from '../../application/composer/execution-profile.js';

const choose = (condition, whenTrue, whenFalse) => (condition ? whenTrue() : whenFalse());

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

export default function ComposerSectionNode(options) {
  const { inputProjection, onRespondInput, themeTokens, contentMaxWidth, streamLoading, sendSubmitting, sendDisabled = false, resolvedUILabels, modeSwitchOptions = [], capabilityCatalog, inputState, onAttachFiles, onSend, onAbort, effectiveComposerChooser, onComposerActionSelect, onComposerChooserDismiss, canonicalActionEntries, runtimeComposerActions, initialModeKey, isRequirementIntakeMode, onModeChanged, userId, displayPolicy, intentEscalationPolicyPopoverContent, contextGaugePopoverContent, contextGaugeHeadline, contextGaugeActivity, contextWindowPercent, shouldRenderModeSuggestion, modeSuggestion, modeSuggestionAction, modeSuggestionActions, tipRules, runtimePreferences, latestUnreadCue, activeArtifactTargetChip = null, onActiveArtifactTargetDismiss = null, composerInputRef } = { sendSubmitting: false, capabilityCatalog: [], effectiveComposerChooser: null, onComposerActionSelect: () => {}, onComposerChooserDismiss: () => {}, canonicalActionEntries: [], runtimeComposerActions: [], initialModeKey: 'auto', backendModeKey: 'auto', activeRequirementIntakeRunId: '', isRequirementIntakeMode: false, displayPolicy: {}, contextGaugeHeadline: '', contextGaugeActivity: null, contextWindowPercent: 0, shouldRenderModeSuggestion: true, modeSuggestion: null, modeSuggestionAction: null, modeSuggestionActions: [], tipRules: [], runtimePreferences: null, latestUnreadCue: null, composerInputRef: null, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const [activeModeKey, setActiveModeKey] = useState(() => resolveComposerModeKey(initialModeKey));
  const [statusOnlyTipHidden, setStatusOnlyTipHidden] = useState(false);
  const [intentEscalationPolicyPopoverOpen, setIntentEscalationPolicyPopoverOpen] = useState(false);
  const inputValue = Object(inputState).inputValue;
  const contextActivityStatus = choose(String(firstTruthy(() => (Object(contextGaugeActivity).state), () => (''))) === 'idle', () => (''), () => (firstTruthy(() => (Object(contextGaugeActivity).status), () => (''))));
  React.useEffect(() => {
    setActiveModeKey(resolveComposerModeKey(initialModeKey));
  }, [initialModeKey]);
  React.useEffect(() => {
    setStatusOnlyTipHidden(false);
  }, [activeModeKey, inputValue]);
  const modeStatusByKey = useMemo(
    () => Object.fromEntries(modeSwitchOptions.map(({ value }) => [value, value === activeModeKey ? 'active' : 'idle'])),
    [activeModeKey, modeSwitchOptions],
  );
  const {
    modeSuggestion: resolvedModeSuggestion,
    modeSuggestionAction: resolvedModeSuggestionAction,
    fromFallback: modeSuggestionFromFallback,
  } = useComposerModeSuggestion({
    inputValue: String(firstTruthy(() => (Object(inputState).inputValue), () => (''))),
    activeModeKey,
    modeSuggestion,
    modeSuggestionAction,
    tipRules,
  });
  const resolvedModeKey = String(firstTruthy(() => (activeModeKey), () => (''))).trim();
  const recommendationPanelState = 'hidden';
  const applyModeSelection = useCallback((nextModeKey) => {
    const normalizedModeKey = resolveComposerModeKey(nextModeKey);
    setActiveModeKey(normalizedModeKey);
    onModeChanged?.(normalizedModeKey);
  }, [onModeChanged]);
  const handleModeSuggestionAction = onComposerActionSelect;
  const handleCanonicalActionSelect = onComposerActionSelect;
  const handleCollectingAnalysisAction = (collectingAnalysisAction) => {
    if (!collectingAnalysisAction) {
      return;
    }
    onComposerActionSelect?.({
      source: 'runtime_action',
      text: String(firstTruthy(() => (Object(collectingAnalysisAction).label), () => (Object(collectingAnalysisAction).actionText), () => (''))).trim(),
      action: collectingAnalysisAction,
    });
  };
  const handleComposerChooserSubmit = useCallback((payload) => {
    return onRespondInput?.({ projection: inputProjection, actionInputValues: payload.actionInputValues });
  }, [inputProjection, onRespondInput]);

  // A selected profile only changes execution preferences, never procurement stage truth.
  const handleSend = useCallback(() => {
    if (sendDisabled) return false;
    return onSend({ streamOptions: {
      ...buildComposerExecutionOptions(resolvedModeKey),
      runtimePreferences,
    } });
  }, [sendDisabled, onSend, resolvedModeKey, runtimePreferences]);
  const handleChooserDismiss = useCallback(() => onComposerChooserDismiss?.({
    composerChooser: effectiveComposerChooser,
  }), [onComposerChooserDismiss, effectiveComposerChooser]);
  const patchEventHealth = useMemo(() => ({ ok: true }), []);
  const applyBehaviorEvent = useCallback(() => {}, []);

  return (
    <ChatWorkspaceComposerSection
      canvasWorkspaceFullscreenMode={false}
      themeTokens={themeTokens}
      isCompactLayout={false}
      contentMaxWidth={contentMaxWidth}
      recommendationPanelState={recommendationPanelState}
      effectiveComposerChooser={effectiveComposerChooser}
      streamLoading={streamLoading}
      sendSubmitting={sendSubmitting}
      sendDisabled={sendDisabled}
      shouldRenderModeSuggestion={shouldRenderModeSuggestion}
      modeSuggestion={resolvedModeSuggestion}
      modeSuggestionAction={resolvedModeSuggestionAction}
      modeSuggestionActions={modeSuggestionActions}
      canonicalActionEntries={canonicalActionEntries}
      onCanonicalActionSelect={handleCanonicalActionSelect}
      modeSuggestionFromFallback={modeSuggestionFromFallback}
      onModeSuggestionAction={handleModeSuggestionAction}
      handleComposerActionSelect={onComposerActionSelect}
      isStatusOnlyModeSuggestion={false}
      statusOnlyTipHidden={statusOnlyTipHidden}
      setStatusOnlyTipHidden={setStatusOnlyTipHidden}
      applyBehaviorEvent={applyBehaviorEvent}
      isRequirementIntakeMode={isRequirementIntakeMode}
      chooserAwaitingAuthoritative={false}
      canonicalAwaitingReason=""
      patchEventHealth={patchEventHealth}
      showCollectingAnalysisCta={false}
      collectingAnalysisAction={null}
      onCollectingAnalysisAction={handleCollectingAnalysisAction}
      attachmentActionError={null}
      resolvedUILabels={resolvedUILabels}
      attachmentConstraintsHint="当前附件仅用于本次消息。最多 10 个，单个不超过 20MB。"
      composerInputRef={composerInputRef}
      modeSwitchOptions={modeSwitchOptions}
      capabilityCatalog={capabilityCatalog}
      resolvedActiveModeKey={activeModeKey}
      modeStatusByKey={modeStatusByKey}
      handleAttachFiles={onAttachFiles}
      inputState={inputState}
      handleModeSelect={applyModeSelection}
      handleSend={handleSend}
      handleAbort={onAbort}
      resolvedComposerPlaceholder="请输入你的需求"
      runtimeComposerActions={runtimeComposerActions}
      onComposerChooserSubmit={handleComposerChooserSubmit}
      onComposerChooserDismiss={handleChooserDismiss}
      sendButtonLabel="发送"
      resolvedUserDisplayName={userId}
      displayPolicy={displayPolicy}
      intentEscalationPolicyPopoverContent={intentEscalationPolicyPopoverContent}
      intentEscalationPolicyPopoverOpen={intentEscalationPolicyPopoverOpen}
      handleIntentEscalationPolicyPopoverOpenChange={setIntentEscalationPolicyPopoverOpen}
      contextGaugePopoverContent={contextGaugePopoverContent}
      contextGaugeHeadline={contextGaugeHeadline}
      contextWindowPercent={contextWindowPercent}
      contextActivityState={firstTruthy(() => (Object(contextGaugeActivity).state), () => (''))}
      contextActivityStatus={choose(firstTruthy(() => (streamLoading), () => (sendSubmitting)), () => ('running'), () => (contextActivityStatus))}
      latestUnreadCue={latestUnreadCue}
      activeTargetChip={activeArtifactTargetChip}
      onActiveTargetDismiss={onActiveArtifactTargetDismiss}
    />
  );
}
